import React, { useState } from 'react';
import EntryImage from '../../components/common/EntryImage';
import { getPublicErrorMessage } from '../../utils/errors';

/**
 * Performs the week start operation.
 *
 * @param {*} date - date value.
 * @returns {*} The derived operation result.
 */
function weekStart(date = new Date()) {
  const result = new Date(date);
  result.setHours(0, 0, 0, 0);
  result.setDate(result.getDate() - result.getDay());
  return result;
}

/**
 * Performs the history date operation.
 *
 * @param {*} workout - workout value.
 * @returns {*} The derived operation result.
 */
function historyDate(workout) {
  return workout.completedAt || workout.workoutDate || workout.updatedAt || '';
}

/**
 * Performs the history date key operation.
 *
 * @param {*} workout - workout value.
 * @returns {*} The derived operation result.
 */
function historyDateKey(workout) {
  const key = String(historyDate(workout)).slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(key)) return '';
  const todayKey = localDateKey(new Date());
  if (key > todayKey && workout.updatedAt) {
    const updatedAt = new Date(workout.updatedAt);
    if (!Number.isNaN(updatedAt.getTime())) {
      const updatedKey = localDateKey(updatedAt);
      if (updatedKey <= todayKey) return updatedKey;
    }
  }
  return key;
}

/**
 * Performs the local date key operation.
 *
 * @param {*} date - date value.
 * @returns {*} The derived operation result.
 */
function localDateKey(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Performs the recent weeks operation.
 *
 * @param {*} history - history value.
 * @returns {*} The derived operation result.
 */
function recentWeeks(history) {
  const start = weekStart();
  return Array.from({ length: 8 }, (_, index) => {
    const week = new Date(start);
    week.setDate(week.getDate() - ((7 - index) * 7));
    const end = new Date(week);
    end.setDate(end.getDate() + 7);
    const startKey = localDateKey(week);
    const endKey = localDateKey(end);
    const count = history.filter((entry) => {
      const dateKey = historyDateKey(entry);
      const completed = String(entry.status || '').trim().toLowerCase() === 'completed' || Boolean(entry.completedAt);
      return completed && dateKey >= startKey && dateKey < endKey;
    }).length;
    return { start: week, count };
  });
}

/** Presents routine schedule/progress and routes starts or past sessions through callbacks. */
export default function FitnessRoutineDetail({ plan, workouts, history = [], historyLoading = false, historyPageInfo, onLoadMoreHistory, onBack, onStart, onLogPast, onOpenWorkout }) {
  const [showLogPast, setShowLogPast] = useState(false);
  const [pastDayId, setPastDayId] = useState(plan.days?.[0]?.id || '');
  const [pastDate, setPastDate] = useState(new Date().toISOString().slice(0, 10));
  const [isLoggingPast, setIsLoggingPast] = useState(false);
  const week = weekStart();
  const relevant = workouts.filter((workout) => workout.planId === plan.id && new Date(`${workout.completedAt || workout.workoutDate || '1970-01-01'}T00:00:00`) >= week);
  const completedIds = new Set(relevant.filter((item) => item.status === 'Completed').map((item) => item.planDayId));
  const mergedHistory = Array.from(new Map([
    ...history,
    ...workouts.filter((workout) => workout.planId === plan.id && String(workout.status || '').toLowerCase() === 'completed'),
  ].map((workout) => [workout.id, workout])).values()).sort((left, right) => historyDateKey(right).localeCompare(historyDateKey(left)));
  const consistency = recentWeeks(mergedHistory);
  const total = plan.days?.length || 0;
  const completed = completedIds.size;
  const frequency = `${plan.daysPerWeek || total}x per week`;

  return (
    <div className="module-wrapper fitness-module movies-module">
      <div className="recipe-detail-view">
        <button type="button" className="recipe-back-link" onClick={onBack}>← Back</button>
        <section className="recipe-detail-hero">
          <EntryImage className="entry-detail-photo" fallbackType="fitness-routine" variant="detail-square" />
          <div><p className="eyebrow">Routine</p><h2>{plan.title}</h2><p>{[plan.goal, frequency, plan.status].filter(Boolean).join(' · ')}</p></div>
          <div className="routine-hero-actions">
            {plan.status === 'Active' && <button type="button" className="btn-primary routine-start-button" onClick={() => onStart(null)}>Start workout</button>}
          </div>
        </section>
        {total > 0 && <section className="recipe-detail-panel"><h3>This week</h3><div className="fitness-routine-progress"><strong>{completed} of {total}</strong><span>workouts completed</span><progress value={completed} max={total} /></div></section>}
        <section className="recipe-detail-panel"><h3>Details</h3><dl className="detail-grid"><div><dt>Goal</dt><dd>{plan.goal || 'Not set'}</dd></div><div><dt>Status</dt><dd>{plan.status || 'Active'}</dd></div><div><dt>Frequency</dt><dd>{frequency}</dd></div>{plan.startDate && <div><dt>Start</dt><dd>{plan.startDate}</dd></div>}{plan.endDate && <div><dt>End</dt><dd>{plan.endDate}</dd></div>}</dl></section>
        <section className="recipe-detail-panel"><h3>Workout days</h3><div className="fitness-routine-days">{(plan.days || []).map((day) => {
          const workout = relevant.find((item) => item.planDayId === day.id);
          const status = workout?.status === 'Completed' ? 'Completed' : workout?.status === 'In Progress' ? 'In progress' : 'Upcoming';
          return <button type="button" className="fitness-routine-detail-day" key={day.id} onClick={() => workout ? onOpenWorkout(workout) : plan.status === 'Active' && onStart(day)}><div><strong>{day.title || 'Workout day'}</strong><span>{[day.weekday, status].filter(Boolean).join(' • ')}</span></div><div className="fitness-detail-exercises">{(day.strengthExercises || []).filter((item) => item.name).map((exercise) => <span key={exercise.id}>{exercise.name}<small>{(exercise.sets || []).length} sets</small></span>)}</div></button>;
        })}</div></section>
        {historyLoading && mergedHistory.length === 0 ? (
          <section className="recipe-detail-panel"><h3>Workout history</h3><div className="routine-history-skeleton"><i /><i /><i /></div></section>
        ) : (
          <section className="recipe-detail-panel">
            <h3>Workout history</h3>
            {mergedHistory.length > 0 ? (
              <>
                <div className="routine-consistency"><div><span>Recent consistency</span><small>Last 8 weeks</small></div><div className="routine-consistency-bars">{consistency.map((weekItem, index) => <span className="routine-consistency-week" key={weekItem.start.toISOString()}><b>{weekItem.count || ''}</b><i className={weekItem.count ? 'active' : ''} style={{ height: weekItem.count ? `${Math.min(12 + weekItem.count * 8, 38)}px` : '8px' }} title={`${weekItem.count} workout${weekItem.count === 1 ? '' : 's'}`} /><small>{index === 0 || index === 7 ? weekItem.start.toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) : ''}</small></span>)}</div>{consistency.every((item) => item.count === 0) && <p className="muted-copy">No completed workouts in the last 8 weeks.</p>}</div>
                <div className="fitness-routine-days">{mergedHistory.map((workout) => (
                  <button type="button" className="fitness-routine-detail-day" key={workout.id} onClick={() => onOpenWorkout(workout)}>
                    <div><strong>{workout.title || 'Workout'}</strong><span>{[historyDate(workout), workout.durationMinutes > 0 && `${workout.durationMinutes} min`, workout.exerciseCount > 0 && `${workout.exerciseCount} exercise${workout.exerciseCount === 1 ? '' : 's'}`, workout.setCount > 0 && `${workout.setCount} set${workout.setCount === 1 ? '' : 's'}`].filter(Boolean).join(' • ')}</span></div><b>›</b>
                  </button>
                ))}</div>
                {historyPageInfo?.hasNextPage && <button type="button" className="btn-secondary cooking-history-more" disabled={historyLoading} onClick={onLoadMoreHistory}>{historyLoading ? 'Loading...' : 'Load more workouts'}</button>}
              </>
            ) : <p className="muted-copy">No completed workouts yet.</p>}
            <button type="button" className="detail-context-action" onClick={() => setShowLogPast(true)}><span aria-hidden="true">＋</span> Log past workout</button>
          </section>
        )}
        {plan.notes && <section className="recipe-detail-panel"><h3>Notes</h3><p className="detail-notes">{plan.notes}</p></section>}
      </div>
      {showLogPast && <div className="modal-backdrop" onMouseDown={() => !isLoggingPast && setShowLogPast(false)}><section className="modal-card fitness-log-past-modal" role="dialog" aria-modal="true" onMouseDown={(event) => event.stopPropagation()}><p className="eyebrow">Routine</p><h2>Log past workout</h2><div className="form-grid"><label className="form-group"><span>Completed on</span><input type="date" max={new Date().toISOString().slice(0, 10)} value={pastDate} onChange={(event) => setPastDate(event.target.value)} /></label><label className="form-group"><span>Workout day</span><select value={pastDayId} onChange={(event) => setPastDayId(event.target.value)}>{(plan.days || []).map((day) => <option value={day.id} key={day.id}>{[day.title || 'Workout day', day.weekday].filter(Boolean).join(' · ')}</option>)}</select></label></div><div className="modal-actions"><button type="button" className="btn-ghost" disabled={isLoggingPast} onClick={() => setShowLogPast(false)}>Cancel</button><button type="button" className="btn-primary" disabled={!pastDayId || !pastDate || isLoggingPast} onClick={async () => { try { setIsLoggingPast(true); const day = (plan.days || []).find((item) => item.id === pastDayId); await onLogPast(day, pastDate); setShowLogPast(false); } catch (error) { window.alert(getPublicErrorMessage(error)); } finally { setIsLoggingPast(false); } }}>{isLoggingPast ? 'Logging…' : 'Log workout'}</button></div></section></div>}
    </div>
  );
}
